// b13 Rumaysa — variant that renders the standalone celestial-library
// birthday "storybook" at /templates/rumaysa.html inside a full-bleed
// iframe (midnight blue + antique gold + lavender; tunnel-book arches,
// drifting books / balloons / butterflies, a splash monogram, a "letter
// from the moon", four year-wish chapters, and a shared wishes wall).
//
// A poetic keepsake — by design it carries NO date / venue / countdown /
// map. The HTML's customizer reads the child's name (first segment of
// ?names), an optional ?letter and ?closing_line, an optional ?chapters
// JSON array, and the ?audio soundtrack. The wishes wall round-trips to
// the real backend guestbook keyed by ?slug.

import type { VariantProps } from "./types";
import { getLocale } from "@/lib/i18n";

const TEMPLATE_HTML = "/templates/rumaysa.html";

export function RumaysaStaticVariant({ t, ctx }: VariantProps) {
  const params = new URLSearchParams();
  // Birthday → a single child name; the HTML takes the first segment.
  if (t.names) params.set("names", t.names);

  // "A letter from the moon" body. Surfaced through the editor as the
  // `letter` extraField on the b13 row in data.ts.
  const letter =
    typeof t.custom?.letter === "string" ? t.custom.letter.trim() : "";
  if (letter) params.set("letter", letter);

  // Footer closing line (extraField `closing_line`).
  const closing =
    typeof t.custom?.closing_line === "string" ? t.custom.closing_line.trim() : "";
  if (closing) params.set("closing_line", closing);

  // Four chapters (title + text) — owner-editable list (extraList
  // `chapters`). An empty array is a meaningful signal (hide the section),
  // so pass the array through verbatim when present.
  const chapters = Array.isArray(t.custom?.chapters) ? t.custom.chapters : null;
  if (chapters) params.set("chapters", JSON.stringify(chapters));

  // Real slug only when published — the in-iframe wishes wall uses it to
  // GET/POST the shared guestbook. Undefined in preview ⇒ sample wishes.
  if (ctx.slug) params.set("slug", ctx.slug);

  // ?audio=/music/<id>.mp3 — owner-picked soundtrack from the curated
  // MusicSelector library. Empty/unset = silent card.
  if (t.musicTrackId) params.set("audio", "/music/" + t.musicTrackId + ".mp3");


  // Demo/catalog preview (no real invitation slug): default the template's
  // in-iframe language to the app locale, so taklifla.com previews render in
  // English (and a RU viewer sees Russian). The HTMLs read ?lang= (all) and
  // ?default_lang= (newer premium ones) — set both. Owner-published
  // invitations (ctx.slug set) keep their own default_lang / built-in default.
  if (!ctx.slug) {
    const _loc = getLocale();
    if (!params.has("lang")) params.set("lang", _loc);
    if (!params.has("default_lang")) params.set("default_lang", _loc);
  }
  const src = `${TEMPLATE_HTML}?${params.toString()}`;

  return (
    <iframe
      src={src}
      title={t.names || "Rumaysa"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100svh",
        border: "none",
        display: "block",
        background: "#0A0E1F",
      }}
    />
  );
}
